import { css } from "@emotion/css";
import styled from "@emotion/styled";
import {
  Badge,
  Button,
  Flex,
  Pagination,
  useMantineColorScheme
} from "@mantine/core";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import type { Column, SortColumn } from "react-data-grid";
import DataGrid, { SelectColumn } from "react-data-grid";

import { useModal } from "@/features/modal/ModalStackManager";
import { useSub } from "@/hooks";

import { mutateSpecifications, specifications } from "@/api/inspect/inspectSpecification/useSpecificationsQuery";
import { specificationsHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchBox, SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import customAlert from "@/features/ui/alert/alert";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import useQueryString from "@/hooks/useQueryString";
import useUpdateUrlParams from "@/hooks/useUpdateUrlParams";
import { theme } from "@/styles/theme";
import { SpcApiSpecificationsSpecificationIdDeleteRequest, SpecificationsGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQuery } from "react-query";
import { InspectSpecificationUpdateForm } from "../form/InspectSpecificationUpdateForm";

interface ColorThemeProps {
  isDarkMode: boolean;
}

export const InspectSpecificationTable = () => {
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);

  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDarkMode: boolean = colorScheme === "light";

  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  const [selectedRows, setSelectedRows] = useState(
    (): ReadonlySet<any> => new Set()
  );
  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });
  const updateUrlParams = useUpdateUrlParams();

  const { openModal } = useModal();

  const isSort = sort.length;

  const { data: InspectSpecifications, refetch } = useQuery(specifications.get({
    query: query,
    search: search.search,
    searchFields: search.searchFields.length
      ? search.searchFields
      : specificationsHeader.map((specification) => specification.value),
    page: activePage,
    pageSize: Number(search.pageSize),
    sort: sort.length ? sort.join(",") : "-id"
  }));

  // 새로운 데이터가 로드될 때, 현재 페이지가 유효하지 않으면 1페이지로 설정
  useEffect(() => {
    if (InspectSpecifications && activePage > (InspectSpecifications?.data?.totalPages ?? 0)) {
      setPage(1);
      setQueryString((prev) => ({
        ...prev,
        page: 1,
      }));
    }
  }, [InspectSpecifications, activePage, setQueryString]);

  // 페이지 Query String으로 공정 이동
  useEffect(() => {
    updateUrlParams({
      page: activePage,
      pageSize: search.pageSize,
      search: search.search,
      searchFields: search.searchFields,
      query: JSON.stringify(query),
      sort: sort
    }, queryDefaultValues);
  }, [activePage, search, query, sort, updateUrlParams]);

  const rows: readonly SpecificationsGet200ResponseRowsInner[] =
    InspectSpecifications?.data.rows ?? [];

  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());

  const { mutate: deleteMutate } = useMutation(
    (params: SpcApiSpecificationsSpecificationIdDeleteRequest) =>
      mutateSpecifications.delete(params).mutationFn(params as any | SpcApiSpecificationsSpecificationIdDeleteRequest),
    {
      onSuccess: () => {
        refetch();
      }
    }
  );

  const columns: readonly Column<SpecificationsGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
      headerCellClass: css`
        & > * {
          justify-content: flex-start;
          padding-left: 24px;
        }
      `,
      cellClass: css`
        .rdg-checkbox-label {
          padding-left: 24px;
        }
      `,
    },
    {
      key: "itemCode",
      name: "품목코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <DetailLink
            onClick={() => {
              openModal(
                <ItemsDetailForm itemCode={formatterProps.row.itemCode} />,
                null,
                ""
              );
            }}
          >
            {formatterProps.row.itemCode}
          </DetailLink>
        );
      },
    },
    {
      key: "routingCode",
      name: "검사종류",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Badge color="blue" radius={2} fw={700} size={"9px"}>
            초중종물 검사
          </Badge>
        );
      },
    },
    {
      key: "summary",
      name: "검사 기준 요약",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const summaryNames = formatterProps.row.criteria.map(
          (data) => data.name
        );
        return (
          <>
            {/* {summaryNames.join(", ")} */}
            {summaryNames.length > 1
              ? summaryNames[0] + " 외 " + (summaryNames.length - 1) + "개"
              : summaryNames[0]}
          </>
        );
      },
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const deleteItemAction = async (tid: number) => {
          if (window.confirm("정말로 삭제하시겠습니까?")) {
            deleteItem(tid);
          }
        };

        // delete item after confirm
        const deleteItem = async (deleteItemId: number) => {
          deleteMutate(
            { specificationId: deleteItemId },
            {
              onSuccess: (res) => {
                if (res.status === 200) {
                  customAlert(
                    "검사표준 삭제에 성공하였습니다.",
                    "삭제 성공",
                    "green"
                  );
                }
              },
              onError: () => {
                customAlert(
                  "검사표준 삭제에 실패하였습니다.",
                  "삭제 실패",
                  "red"
                );
              },
            }
          );
        };

        return (
          <OptionBox>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconEdit />}
              color="yellow"
              onClick={() => {
                openModal(
                  <InspectSpecificationUpdateForm
                    data={{
                      itemCode: formatterProps.row.itemCode,
                    }}
                  />,
                  "",
                  "검사 표준 수정",
                  true
                )
              }
              }
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconTrash />}
              color="red"
              onClick={() => {
                deleteItemAction(formatterProps?.row?.id as any);
              }}
            ></OptionBtn>
          </OptionBox>
        );
      },
    },
  ];

  useSub("deleteSelectedItems", () => {
    const rows = Array.from(selectedRowsRef.current);
    if (!rows.length) return;
    const deleteRows: number[] = Array.from(rows[rows.length - 1].values());
    if (window.confirm("정말로 삭제하시겠습니까?")) {
      deleteRows.forEach((row: number) => {
        deleteMutate({ specificationId: row });
      });
      selectedRowsRef.current = new Set();
    }
  });

  return (
    <RoutingTableWrapper>
      <GridWrapper isDarkMode={isDarkMode}>
        <TableWrapper>
          <RetrieveWrapper>
            <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
              <CustomFilter filterType={specificationsHeader} setQuery={setQuery} query={query} />
              <CustomSorter sorterType={specificationsHeader} setSort={setSort} sort={sort} />
            </Flex>
            <SearchBox
              searchType={specificationsHeader}
              setSearch={setSearch}
              search={search}
            />
          </RetrieveWrapper>
          <DataGrid
            columns={columns ?? []}
            rows={rows ?? []}
            rowHeight={40}
            rowKeyGetter={(row: SpecificationsGet200ResponseRowsInner) =>
              row.id
            }
            sortColumns={sortColumns}
            selectedRows={selectedRows}
            onSelectedRowsChange={(e) => {
              setSelectedRows(e);
              const newSelectedRows = new Set(selectedRowsRef.current);
              newSelectedRows.add(e);
              selectedRowsRef.current = newSelectedRows;
            }}
          />
        </TableWrapper>
        <PaginationWrapper>
          <Pagination
            onChange={setPage}
            value={activePage}
            total={InspectSpecifications?.data?.totalPages ?? 0}
            size="lg"
            radius="sm"
          />
        </PaginationWrapper>
      </GridWrapper>
    </RoutingTableWrapper>
  );
};

const RoutingTableWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const GridWrapper = styled.div<ColorThemeProps>`
  & *[role="grid"] {
    height: 440px;
    --rdg-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-header-background-color: ${(props) =>
    props.isDarkMode ? "white" : "none"};
    --rdg-color: ${(props) => (props.isDarkMode ? "black" : "white")};
    --rdg-row-hover-background-color: ${(props) =>
    props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;
const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;
const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const RetrieveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  justify-content: space-between;
`;
